import { Color, ColorFill, Container, SvgGraphics } from '@tencent/alloy-renderer';
import { ChartEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/chart-edit-logic';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { IChartElement } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { CANVAS_CHART_COMPONENT_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { isLineShape } from './chart-elements/base';

export interface HitAreaProps {
    path: string;
    x?: number;
    y?: number;
    element: IChartElement;
    factoryOptions: InterfaceFactoryOptions;
}

export interface HitAreaContainerProps {
    width: number;
    height: number;
    factoryOptions: InterfaceFactoryOptions;
    hitAreas: HitAreaProps[]
}

// 两次点击间隔大于550毫米为"非双击"
const SINGLE_CLICK_INTERVAL = 550;

export class HitAreaContainer extends Container {
    public hitAreaList: HitArea[];
    public blankArea: SvgGraphics;
    public factoryOptions: InterfaceFactoryOptions;

    public constructor(props: HitAreaContainerProps) {
        super();
        this.updateProps(props);
    }

    public updateProps(props: HitAreaContainerProps) {
        const { width, height, hitAreas, factoryOptions } = props;
        this.factoryOptions = factoryOptions;
        // TODO 暂时每次绘制都重新绘制HitArea，后续再处理增量更新的问题。因为 拖动、删除模块，都会需要更新diff hitArea
        if (this.blankArea) {
            this.blankArea.dispose();
            this.removeChild(this.blankArea);
        }
        this.blankArea = this.createBlankArea(width, height);
        this.addChild(this.blankArea);

        if (this.hitAreaList) {
            this.hitAreaList.forEach((hitArea) => {
                hitArea.dispose();
                this.removeChild(hitArea);
            });

            this.hitAreaList = [];
        }
        this.hitAreaList = hitAreas.map((hitAreaProp) => {
            const hitArea = new HitArea(hitAreaProp);
            this.addChild(hitArea);

            return hitArea;
        });
    }

    /**
     * 创建空白点击区域
     * @param width
     * @param height
     * @private
     */
    private createBlankArea(width, height): SvgGraphics {
        const svgPath = `M 0 0 L ${width} 0 L ${width} ${height} L 0 ${height} Z`;
        const svgGraphics = new SvgGraphics(svgPath);
        svgGraphics.fill = new ColorFill(Color.Transparent);
        svgGraphics.onMouseDown(() => {
            this.chartEditLogic?.handlePlotBlankMouseDown();
        });
        return svgGraphics;
    }

    private get chartEditLogic(): ChartEditLogic | undefined {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions) as ChartEditLogic;
    }
}

export class HitArea extends SvgGraphics {
    public factoryOptions: InterfaceFactoryOptions;
    private element: IChartElement;
    private lastClickTimestamp: number;

    public constructor(props: HitAreaProps) {
        super();
        let color = new ColorFill(Color.Transparent);
        // 用于 e2e定位元素
        this.id = `${CANVAS_CHART_COMPONENT_ID_PREFIX}-${props.element.id}`;
        // 辅助代码，用于肉眼颜色区分
        const isShowHit =  /hit=1/.test(location.search);
        if (isShowHit) {
            const randomColor = Math.round(Math.random() * 0xFFFFFF);
            const fill = `#${randomColor.toString(16).padStart(6, '0')}`;
            color = new ColorFill(Color.fromHex(fill));
        }

        if (isLineShape(props.element.shapeType)) {
            this.strokeWidth = 15;
            this.stroke = color;
        } else {
            this.fill = color;
        }

        this.updateProps(props);

        this.onMouseDown(this.handleMouseDown);
    }

    public updateProps(props: HitAreaProps) {
        this.path = props.path;
        this.x = props.x || 0;
        this.y = props.y || 0;
        this.element = props.element;
        this.factoryOptions = props.factoryOptions;
    }

    private handleMouseDown = (e) => {
        const now = Date.now();

        this.chartEditLogic.handleElementSelected(this.element, this.isSingleClick(now));

        this.recordClickTimestamp(now);
    };

    private get chartEditLogic(): ChartEditLogic {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions) as ChartEditLogic;
    }

    private recordClickTimestamp(time: number) {
        this.lastClickTimestamp = time;
    }

    private isSingleClick(currentTime: number, intervalLimit = SINGLE_CLICK_INTERVAL) {
        return !this.lastClickTimestamp || (currentTime - this.lastClickTimestamp) >= intervalLimit;
    }
}
